import {useState, useEffect, useCallback} from 'react';
import axiosInstance from '../../../Config/axiosInstance';
import {Container, Paper, Table, TableBody, TableCell, TableContainer,TableHead, TableRow, Button, Typography, CircularProgress,
  Alert, Box, Menu, ListItemIcon, ListItemText, OutlinedInput, Select, Checkbox, FormControl, InputLabel, MenuItem} from '@mui/material';
import {Save, PictureAsPdf, GetApp, Assessment} from '@mui/icons-material';
import * as ExcelJS from 'exceljs';
import './ExternalResourceReport.css';
import logoImg from '../../../Images/logo_pmpc.png';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ExternalResourceReport = () => {
    const [filters, setFilters] = useState({supplier: ''});
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [error, setError] = useState(null);   
    const [reportGenerated, setReportGenerated] = useState(false);

    const loadSupplierOptions = useCallback(async () => {
        setLoadingOptions(true);
        setError('');
        try {
            const response = await axiosInstance.get('/external-resource-report/');
            const options = response.data.map(supplier => ({value: supplier, label: supplier}));
            setSupplierOptions(options);
        } catch (err) {
            console.error('Error loading suppplier options:', err);
            setError('Error loading supplier options');
        } finally {
            setLoadingOptions(false);
        }
    }, []);

    useEffect(() => {
        loadSupplierOptions();
    }, [loadSupplierOptions]);

        const handleSubmit = async (e) => {
        e.preventDefault();
        if(!filters.supplier){
            setError('Please select a supplier');
            setReportData(null);
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get('/external-resource-report/', {
                params: {supplier: filters.supplier}
            });
            
            console.log('API Response:', response.data);
            
            if(!response.data || response.data.length === 0) {
                setError('No data found for the selected supplier');
                setReportData(null);
                return;
            }
            
            setReportData(response.data);
            setReportGenerated(true);
        } catch (err) {
            console.error('Error generating report:', err);
            setError(`Error: ${err.response?.data?.message || err.message}`);
            setReportData(null);
        } finally {
            setLoading(false);
        }
    }

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const formatNumber = (num) => {
        if (num == null || num === undefined || num === '' || isNaN(Number(num))) {
        return '0.00';
        }
        const number = typeof num === 'string' ? parseFloat(num) : num;
        return new Intl.NumberFormat('en-EN', { 
        maximumFractionDigits: 2, 
        minimumFractionDigits: 2 
        }).format(number);
    };

    const exportToExcel = async (data = null) => {
        if (!reportData) {
            alert('No data to export');
            return;
        }

        const exportData = data || reportData;
        const title = `External Resource Report - ${filters.supplier}`;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Report');

        //logo
        try {
            sheet.mergeCells('A1:A4');
            const logo = workbook.addImage({
                buffer: await fetch(logoImg).then(res => res.arrayBuffer()),
                extension: 'png',
            });

            sheet.addImage(logo, {
                tl: { col: 0, row: 0 },
                br: { col: 1, row: 4 }
            });
        } catch (error) {
            console.warn('Logo could not be loaded:', error);
        }

        //titlu
        sheet.mergeCells('B1:F4');
        const titleCell = sheet.getCell('B1');
        titleCell.value = title;
        titleCell.font = { size: 14, bold: true };
        titleCell.alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true
        };

        //rand liber
        sheet.insertRow(5, []);

        //header
        const headerRowNumber = 6;
        const headers = [
            'Resource',
            'Project Code',
            'Cost Element ID',
            'Supplier Cost Without VAT',
            'Client Amount Without VAT',
            'Invoiced Client'
        ];

        const headerRow = sheet.getRow(headerRowNumber);
        headerRow.values = headers;
        headerRow.height = 30;
        headerRow.font = { bold: true };

        headerRow.eachCell((cell, colNumber) => {
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: colNumber === 1
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE8E8E8' }
            };
        });

        //date
        let currentRow = headerRowNumber + 1;

        exportData.forEach(item => {
            const row = sheet.getRow(currentRow);

            row.values = [
                item.supplier,
                item.project_code,
                item.cost_element_id,
                Number(item.cost_offer_without_vat || 0),
                Number(item.cost_po_without_vat || 0),
                Number(item.invoiced_client || 0)
            ];

            row.eachCell((cell, colNumber) => {
                cell.alignment = {
                    vertical: colNumber === 1 ? 'top' : 'middle',
                    horizontal: colNumber >= 4 ? 'right' : 'left',
                    wrapText: colNumber === 1
                };

                if (colNumber >= 4) {
                    cell.numFmt = '#,##0.00';
                }
            });

            currentRow++;
        });

        //prima coloana fixa
        sheet.getColumn(1).width = 30;

        //restul coloanelor autowidth
        sheet.columns.forEach((column, index) => {
            if (index === 0) return;

            let maxLength = 10;

            column.eachCell({ includeEmpty: true }, cell => {
                let value = cell.value;
                if (value === null || value === undefined) return;

                if (typeof value === 'object' && value.formula) {
                    value = value.result;
                }

                maxLength = Math.max(maxLength, value.toString().length);
            });

            column.width = Math.min(maxLength + 2, 25);
        });

        //salvare
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const today = new Date().toISOString().split('T')[0];
        const fileName = `External_Resource_Report_${filters.supplier}_${today}.xlsx`;
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    };    

    const exportPDF = async () => {
        if(!reportData) {
            alert('No data to export!');
            return;
        }

        //pdf portret
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const logo = "/logo_pmpc.png";

        //logo
        pdf.addImage(logo, "PNG", 8, 3, 28, 8);

        //dimensiuni elemente decorative
        const triangleWidth = 20;
        const totalWidth = pdfWidth * 0.25;
        const barWidth = totalWidth - triangleWidth;

        const barHeight = 1.2; //bara scurta + triunghi
        const secondBarHeight = 2; //bara lunga
        const barTop = 15;
        const barBottom = barTop + barHeight;

        //bara scurta
        pdf.setFillColor(23, 52, 88);
        const barOverlap = 1;
        pdf.rect(0, barTop, barWidth + barOverlap, barHeight, "F");

        //triunghi
        pdf.triangle(
            barWidth, barBottom,
            barWidth + triangleWidth, barBottom,
            barWidth, barTop,
            "F" 
        );

        //suprapunere
        const overlap = 0.6;
        const headerLongBarTop = barBottom - overlap;

        //bara lunga full-width (header)
        pdf.rect(0, headerLongBarTop, pdfWidth, secondBarHeight, "F");
        const dottedLineY = headerLongBarTop + secondBarHeight;

        //titlu
        const reportTitle = `External Resource Report - ${filters.supplier}`;
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(reportTitle, pdfWidth / 2, dottedLineY + 12, {align: "center"});

        const contentTop = dottedLineY + 20;
        let currentY = contentTop;

        const tableData = reportData.map(item => [
            item.supplier,
            item.project_code,
            item.cost_element_id,
            formatNumber(item.cost_offer_without_vat),
            formatNumber(item.total_invoices_without_vat),
            formatNumber(item.cost_po_without_vat),
            formatNumber(item.invoiced_client)
        ]);

        const tableHeaders = ["Resource", "Project Code", "Cost Element ID", "Supplier Cost Without VAT", "Supplier Invoiced Without VAT", "Client Amount Without VAT", "Invoiced Client"];

        const columnStyles = {
            0: {cellWidth: 45},
            1: {cellWidth: 45},
            2: {cellWidth: 25, halign: 'center'},
            3: {cellWidth: 20, halign: 'right'},
            4: {cellWidth: 20, halign: 'right'},
            5: {cellWidth: 20, halign: 'right'},
            6: {cellWidth: 20, halign: 'right'},
        };

        //adauga tabelul
        autoTable(pdf, {
            startY: currentY,
            head: [tableHeaders],
            body: tableData,
            styles: {
                fontSize: 9, 
                cellPadding: 2,
                overflow: "linebreak",
                lineColor: [180, 180, 180],
                lineWidth: 0.25,
                valign: "middle",
                textColor: [0, 0, 0],
            },
            headStyles: {
                fillColor: [232, 232, 232],
                textColor: 0, 
                fontStyle: "bold",
                halign: "center",
            },
            columnStyles: columnStyles, 
            alternateRowStyles: {
                fillColor: [245, 247, 250]
            },
            margin: {left: 10, right: 10}, 
            tableWidth: 'auto',
            theme: 'grid',
            didDrawPage: function(data) {
                if (data.cursor) {
                    currentY = data.cursor.y;
                }
            }
        });

        //footer pentru toate paginile
        const totalPages = pdf.internal.getNumberOfPages();
        
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            
            pdf.setFillColor(23, 52, 88);

            const bottomOffset = 12; 
            const longBarTop = pdfHeight - bottomOffset; 

            //suprapunere
            const verticalOverlapFooter = 0.6;

            //bara lunga jos
            pdf.rect(
            0,
            longBarTop - verticalOverlapFooter + 0.1,
            pdfWidth,
            secondBarHeight,
            "F"
            );

            //bara scurta + triunghi
            const shortBarTop = longBarTop + secondBarHeight - verticalOverlapFooter;
            const shortBarBottom = shortBarTop + barHeight;

            //bara scurta 
            const barOverlapFooter = 1;
            pdf.rect(
            0,
            shortBarTop,
            barWidth + barOverlapFooter,
            barHeight,
            "F"
            );

            //triunghiul din partea dreapta jos
            pdf.triangle(
            barWidth, shortBarTop,                    
            barWidth + triangleWidth, shortBarTop,        
            barWidth, shortBarBottom,                      
            "F"
            );

            //pagina curenta/ total pagini
            pdf.setFontSize(7);
            pdf.setFont("helvetica", "normal");
            pdf.text("Generated by PMPC ERP", 10, pdfHeight - 3);
            const today = new Date();
            const currentDate = today.toLocaleDateString("ro-RO");
            pdf.text(`${currentDate} `, pdfWidth - 30, pdfHeight - 3);

            pdf.text(`Page ${i} of ${totalPages}`, pdfWidth / 2, pdfHeight - 3, { align: "center" });
        }

        //save pdf
        const today = new Date().toISOString().split('T')[0]; // "2026-01-02"
        const fileName = `ExternalResourceReport_${filters.supplier}_${today}.pdf`;
        pdf.save(fileName);
    }

    return (
        <Container maxWidth={false} disableGutters className="rep-ext-resource ext-res">
            <Container maxWidth={false} disableGutters className="ext-res-container">
                <Typography variant='h4' gutterBottom marginTop='20px'>
                    External Resource Report
                </Typography>

                <Paper className='filters-paper'>
                    <form onSubmit={handleSubmit}>
                        <Box display='flex' alignItems="center" justifyContent="space-between" flex-wrap="wrap" gap={2}>
                            <Box display='flex' alignItems='center' gap={2}>
                                <FormControl 
                                size="small"
                                sx={{minWidth: 350}}
                                required
                                disabled={loadingOptions || supplierOptions.length === 0}  
                                >
                                    <InputLabel id='supplier-label'>Resource (Supplier)</InputLabel>
                                    <Select
                                    labelId='supplier-label'
                                    value={filters.supplier}
                                    onChange={(e) => setFilters(prev =>  ({...prev, supplier: e.target.value}))}
                                    displayEmpty
                                    input={<OutlinedInput label="Resource (Supplier)"/>}
                                    renderValue={(selected) => selected ? selected : ''}
                                    >
                                        {loadingOptions && (
                                            <MenuItem disabled>
                                                <CircularProgress size={20}/>
                                            </MenuItem>
                                        )}
                                        {supplierOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                <Checkbox checked={filters.supplier === option.value} />
                                                <ListItemText primary={option.label}/>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Button
                                    type='submit'
                                    variant='contained'
                                    color='primary'
                                    size='small'
                                    className='generate-button'
                                    startIcon={loading ? <CircularProgress size={20}/>:<Assessment/>}
                                    disable={loadingOptions || loading}
                                >
                                    {loading ? 'Generating...' : 'Generate Report'}
                                </Button>
                            </Box>

                            <Box>
                                <Button
                                    variant='contained'
                                    size='small'
                                    onClick={handleClick}
                                    className='save-button'
                                    startIcon={<Save/>}
                                    disabled={!reportGenerated}
                                >
                                    Save As
                                </Button>

                                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                                    <MenuItem onClick={() => { exportPDF(); handleClose(); }}>
                                        <ListItemIcon><PictureAsPdf fontSize='small'/></ListItemIcon>
                                        <ListItemText>Save as PDF</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={() => { exportToExcel(); handleClose(); }}>
                                        <ListItemIcon><GetApp fontSize='small'/></ListItemIcon>
                                        <ListItemText>Save as Excel</ListItemText>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        </Box>
                         
                        {error && (
                            <Alert severity='error' className='error-alert' sx={{mt:2}}>
                                {error}
                            </Alert>
                        )}
                    </form>
                </Paper>

                {reportGenerated && reportData && (
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3, mt: 3}}>
                        <Paper className='filters-paper' sx={{p:2}}>
                            <>
                            <TableContainer className='report-table-container'>
                                <Table stickyHeader size='small' sx={{minWidth: 800}}>
                                    <TableHead>
                                        <TableRow className='table-header'>
                                            <TableCell className='table-header-cell'>Resource</TableCell>
                                            <TableCell className='table-header-cell'>Project Code</TableCell>
                                            <TableCell className='table-header-cell' align='center'>Cost Element ID</TableCell>
                                            <TableCell className='table-header-cell' align='center'>Supplier Cost Without VAT</TableCell>
                                            <TableCell className='table-header-cell' align='center'>Supplier Invoiced Without VAT</TableCell>
                                            <TableCell className='table-header-cell' align='center'>Client Amount Without VAT</TableCell>
                                            <TableCell className='table-header-cell' align='center'>Invoiced Client</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{row.supplier}</TableCell>
                                            <TableCell>{row.project_code}</TableCell>
                                            <TableCell align='center'>{row.cost_element_id}</TableCell>
                                            <TableCell align='center'>{formatNumber(row.cost_offer_without_vat)}</TableCell>
                                            <TableCell align='center'>{formatNumber(row.total_invoices_without_vat)}</TableCell>
                                            <TableCell align='center'>{formatNumber(row.cost_po_without_vat)}</TableCell>
                                            <TableCell align='center'>{formatNumber(row.invoiced_client)}</TableCell>
                                        </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            </>
                        </Paper>
                    </Box>
                )}

                {reportGenerated && !reportData && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                    Report generated but no data available to display.
                    </Alert>
                )}
        
                {reportGenerated && reportData && (
                    <Box className="report-footer">
                    <Typography variant="caption" className="footer-text">
                        Generated on {reportData.report_date || new Date().toLocaleString('ro-RO')} |{' '}
                        Resource (Supplier): {filters.supplier}
                    </Typography>
                    </Box>
                )}
            </Container>
        </Container>
    )
}

export default ExternalResourceReport;